import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Node as TiptapNode, mergeAttributes, Extension, nodePasteRule, InputRule } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import FontFamily from '@tiptap/extension-font-family';
import Paragraph from '@tiptap/extension-paragraph';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import {
    Badge, Box, Text, Popover, ScrollArea, Group, BoxProps, Modal, TextInput, Stack, Button, Textarea,
    Divider, Portal
} from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import {
    IconHtml, IconLink, IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified
} from '@tabler/icons-react';
import classes from './ScRichTextEditorV2.module.css';
import { Color } from "@tiptap/extension-color";
import { TextStyle } from '@tiptap/extension-text-style';
import { ReplacementTagBadge } from './ReplacementTagBadge';

export interface ReplacementTagItem {
    Group: string;
    Name: string;
    Description: string;
    Module: null | number;
}

const EMPTY_TAGS: ReplacementTagItem[] = [];

const CustomTextStyle = TextStyle.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

const CustomParagraph = Paragraph.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

const CustomImage = Image.extend({
    addOptions() {
        return {
            ...this.parent?.(),
            allowBase64: true,
        };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            src: {
                default: null,
                parseHTML: (element) => element.getAttribute('src'),
                renderHTML: (attributes) => {
                    if (!attributes.src) return {};
                    return { src: attributes.src };
                },
            },
            alt: {
                default: null,
                parseHTML: (element) => element.getAttribute('alt'),
                renderHTML: (attributes) => {
                    if (!attributes.alt) return {};
                    return { alt: attributes.alt };
                },
            },
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
            width: {
                default: null,
                parseHTML: (element) => element.getAttribute('width'),
                renderHTML: (attributes) => {
                    if (!attributes.width) return {};
                    return { width: attributes.width };
                },
            },
            height: {
                default: null,
                parseHTML: (element) => element.getAttribute('height'),
                renderHTML: (attributes) => {
                    if (!attributes.height) return {};
                    return { height: attributes.height };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'img[src]',
            },
        ];
    },
});

const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

const CustomTableRow = TableRow.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

const CustomTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: {
                default: null,
                parseHTML: (element) => element.getAttribute('style'),
                renderHTML: (attributes) => {
                    if (!attributes.style) return {};
                    return { style: attributes.style };
                },
            },
            class: {
                default: null,
                parseHTML: (element) => element.getAttribute('class'),
                renderHTML: (attributes) => {
                    if (!attributes.class) return {};
                    return { class: attributes.class };
                },
            },
        };
    },
});

// Helper to convert {{tag}} plain text to our internal HTML representation
const convertTagsToHtml = (content: string, isPlainText: boolean, replacementTags: ReplacementTagItem[]) => {
    if (!content) return '';
    
    // Use a broad regex to convert all {{tag}} patterns to badges
    const tagRegex = /\{\{([^}]+)\}\}/g;
    
    let processed = content.replace(tagRegex, (match, name) => {
        const isValid = replacementTags.some(t => t.Name === name);
        if (isValid) {
            return `<span data-replacement-tag="true" data-name="${name}"></span>`;
        }
        return match;
    });

    if (isPlainText) {
        // In plain text mode, preserve newlines for Tiptap
        processed = processed.replace(/\n/g, '<br />');
    }
    return processed;
};

const processOutput = (html: string) => {
    if (!html) return '';
    return html.replace(/<span[^>]*data-name="([^"]*)"[^>]*data-replacement-tag="true"[^>]*>.*?<\/span>/g, '{{$1}}')
               .replace(/<span[^>]*data-replacement-tag="true"[^>]*data-name="([^"]*)"[^>]*>.*?<\/span>/g, '{{$1}}');
};

// ReplacementTag Node Extension
export const ReplacementTag = TiptapNode.create({
    name: 'replacementTag',
    group: 'inline',
    inline: true,
    selectable: true,
    atom: true,

    addOptions() {
        return {
            allowedNames: [] as string[],
        };
    },

    addAttributes() {
        return {
            name: {
                default: null,
                parseHTML: element => element.getAttribute('data-name'),
                renderHTML: attributes => ({ 
                    'data-name': attributes.name,
                    'data-replacement-tag': 'true'
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-replacement-tag]',
                getAttrs: element => {
                    const name = (element as HTMLElement).getAttribute('data-name');
                    return { name };
                }
            },
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        return ['span', mergeAttributes(HTMLAttributes, { class: classes.replacementTagNode }), node.attrs.name ?? ''];
    },

    renderText({ node }) {
        return `{{${node.attrs.name}}}`;
    },

    addInputRules() {
        const { type } = this;
        return [
            new InputRule({
                find: /\{\{([^}]+)\}\}$/,
                handler: ({ state, range, match }) => {
                    const { tr } = state;
                    const name = match[1];
                    const { allowedNames } = this.options;
                    if (name && allowedNames.includes(name)) {
                        tr.replaceWith(range.from, range.to, type.create({ name }));
                    } else {
                        return null;
                    }
                },
            }),
            new InputRule({
                find: /\{\{$/,
                handler: ({ state, range, match }) => {
                    const { tr, selection } = state;
                    const { $from } = selection;
                    const textAfter = state.doc.textBetween(range.to, $from.end(), undefined, '\uffff');
                    const matchAfter = textAfter.match(/^([^}]+)\}\}/);
                    if (matchAfter) {
                        const name = matchAfter[1];
                        const { allowedNames } = this.options;
                        if (name && allowedNames.includes(name)) {
                            const endPos = range.to + matchAfter[0].length;
                            tr.replaceWith(range.from, endPos, type.create({ name }));
                        } else {
                            return null;
                        }
                    } else {
                        return null;
                    }
                },
            }),
        ];
    },

    addPasteRules() {
        return [
            nodePasteRule({
                find: /\{\{([^}]+)\}\}/g,
                type: this.type,
                getAttributes: match => {
                    const name = match[1];
                    const { allowedNames } = this.options;
                    if (name && allowedNames.includes(name)) {
                        return { name };
                    }
                    return null;
                },
            }),
        ];
    },

    addProseMirrorPlugins() {
        const { type } = this;

        return [
            new Plugin({
                key: new PluginKey('replacementTagAutoConvert'),
                appendTransaction: (transactions, oldState, newState) => {
                    if (!transactions.some(tr => tr.docChanged)) return null;
                    
                    const { allowedNames } = this.options;
                    if (allowedNames.length === 0) return null;

                    const { tr } = newState;
                    const replacements: { from: number, to: number, name: string }[] = [];

                    newState.doc.descendants((node, pos) => {
                        if (node.isText && node.text) {
                            const regex = /\{\{([^}]+)\}\}/g;
                            let match;
                            while ((match = regex.exec(node.text)) !== null) {
                                const name = match[1];
                                if (allowedNames.includes(name)) {
                                    replacements.push({
                                        from: pos + match.index,
                                        to: pos + match.index + match[0].length,
                                        name
                                    });
                                }
                            }
                        }
                    });

                    if (replacements.length === 0) return null;

                    let modified = false;
                    // Apply replacements from end to start to avoid position shifting
                    replacements.sort((a, b) => b.from - a.from).forEach(r => {
                        tr.replaceWith(r.from, r.to, type.create({ name: r.name }));
                        modified = true;
                    });

                    return modified ? tr : null;
                }
            })
        ];
    },

});

const DisableEnter = Extension.create({
    name: 'disableEnter',
    addKeyboardShortcuts() {
        return {
            Enter: () => true,
            'Shift-Enter': () => true,
        };
    },
});

const CustomAlignLeftControl = () => {
    const { editor } = useRichTextEditorContext();
    const active = editor?.isActive({ textAlign: 'left' }) || (
        editor &&
        !editor.isActive({ textAlign: 'center' }) &&
        !editor.isActive({ textAlign: 'right' }) &&
        !editor.isActive({ textAlign: 'justify' })
    );

    return (
        <RichTextEditor.Control
            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
            active={!!active}
            aria-label="Align left"
            title="Align left"
        >
            <IconAlignLeft stroke={1.5} size="1rem" />
        </RichTextEditor.Control>
    );
};

const CustomAlignCenterControl = () => {
    const { editor } = useRichTextEditorContext();
    return (
        <RichTextEditor.Control
            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
            active={editor?.isActive({ textAlign: 'center' })}
            aria-label="Align center"
            title="Align center"
        >
            <IconAlignCenter stroke={1.5} size="1rem" />
        </RichTextEditor.Control>
    );
};

const CustomAlignRightControl = () => {
    const { editor } = useRichTextEditorContext();
    return (
        <RichTextEditor.Control
            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
            active={editor?.isActive({ textAlign: 'right' })}
            aria-label="Align right"
            title="Align right"
        >
            <IconAlignRight stroke={1.5} size="1rem" />
        </RichTextEditor.Control>
    );
};

const CustomAlignJustifyControl = () => {
    const { editor } = useRichTextEditorContext();
    return (
        <RichTextEditor.Control
            onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
            active={editor?.isActive({ textAlign: 'justify' })}
            aria-label="Align justify"
            title="Align justify"
        >
            <IconAlignJustified stroke={1.5} size="1rem" />
        </RichTextEditor.Control>
    );
};

const CustomLinkControl = () => {
    const { editor } = useRichTextEditorContext();
    const [opened, setOpened] = useState(false);
    const [text, setText] = useState('');
    const [url, setUrl] = useState('');

    const handleOpen = () => {
        if (!editor) return;

        if (editor.isActive('link')) {
            editor.chain().extendMarkRange('link').focus().run();
        }

        const { from, to } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to);
        const attrs = editor.getAttributes('link');

        setText(selectedText || '');
        setUrl(attrs.href || '');
        setOpened(true);
    };

    const handleApply = () => {
        if (!editor) return;

        if (url) {
            let finalUrl = url;
            if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:') && !url.startsWith('/') && !url.startsWith('#')) {
                finalUrl = 'https://' + url;
            }

            editor.chain().focus().extendMarkRange('link').insertContent({
                type: 'text',
                text: text || url,
                marks: [{
                    type: 'link',
                    attrs: {
                        href: finalUrl,
                    }
                }]
            }).setLink({ href: finalUrl }).run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        setOpened(false);
    };

    return (
        <>
            <RichTextEditor.Control
                onClick={handleOpen}
                active={editor?.isActive('link')}
                title="Link"
            >
                <IconLink stroke={1.5} size="1rem" />
            </RichTextEditor.Control>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Insert Link"
                size="sm"
                withinPortal
            >
                <Stack onClick={(e) => e.stopPropagation()}>
                    <TextInput
                        label="Text to display"
                        placeholder="Text"
                        value={text}
                        onChange={(e) => setText(e.currentTarget.value)}
                        autoFocus
                    />
                    <TextInput
                        label="Link URL"
                        placeholder="https://example.com"
                        value={url}
                        onChange={(e) => setUrl(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleApply();
                            }
                        }}
                    />
                    <Group justify="flex-end">
                        <Button variant="subtle" onClick={() => setOpened(false)}>Cancel</Button>
                        <Button onClick={handleApply}>Apply</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

const HtmlEditorControl = ({ replacementTags }: { replacementTags: ReplacementTagItem[] }) => {
    const { editor } = useRichTextEditorContext();
    const [opened, setOpened] = useState(false);
    const [html, setHtml] = useState('');

    const handleOpen = () => {
        if (!editor) return;
        const currentHtml = editor.getHTML();
        setHtml(processOutput(currentHtml));
        setOpened(true);
    };

    const handleApply = () => {
        if (!editor) return;
        const internalHtml = convertTagsToHtml(html, false, replacementTags);
        // Pass emitUpdate=true so the editor's onUpdate fires and the parent
        // form value/validation are updated immediately after applying the HTML.
        editor.commands.setContent(internalHtml, true);
        setOpened(false);
    };

    return (
        <>
            <RichTextEditor.Control
                onClick={handleOpen}
                title="Edit HTML"
            >
                <IconHtml stroke={1.5} size="1rem" />
            </RichTextEditor.Control>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title="Edit HTML"
                size="xl"
                withinPortal
            >
                <Stack onClick={(e) => e.stopPropagation()}>
                    <Textarea
                        // label="Raw HTML"
                        placeholder="<html>...</html>"
                        value={html}
                        onChange={(e) => setHtml(e.currentTarget.value)}
                        autosize
                        minRows={10}
                        maxRows={20}
                        maw={'100%'}
                        styles={{
                            input: {
                                fontFamily: 'monospace',
                                fontSize: 'var(--mantine-font-size-xs)',
                            }
                        }}
                    />
                    <Group justify="flex-end">
                        <Button variant="subtle" onClick={() => setOpened(false)}>Cancel</Button>
                        <Button onClick={handleApply}>Update</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export type ScRichTextEditorV2Mode = 'email' | 'sms' | 'subject';

interface ScRichTextEditorV2Props extends BoxProps {
    value?: string;
    onChange?: (value: string) => void;
    mode?: ScRichTextEditorV2Mode;
    placeholder?: string;
    minHeight?: number | string;
    maxHeight?: number | string;
    error?: string | null;
    hideError?: boolean;
    onBlur?: () => void;
    showHtmlEditButton?: boolean;
    stickyOffset?: number;
    setIsEmpty?: (empty: boolean) => void;
    autoFocus?: boolean;
    replacementTags?: ReplacementTagItem[];
    allReplacementTags?: ReplacementTagItem[];
    unstyled?: boolean;
    bg?: string;
}

interface EditorUIProps {
    editor: any;
    mode: ScRichTextEditorV2Mode;
    unstyled: boolean;
    error: string | null;
    hideError: boolean;
    bg: string;
    stickyOffset: number;
    showHtmlEditButton: boolean;
    replacementTags: ReplacementTagItem[];
    allReplacementTags: ReplacementTagItem[];
    minHeight?: number | string;
    maxHeight?: number | string;
}

const EditorUI = ({ 
    editor, mode, unstyled, error, hideError, bg, stickyOffset, showHtmlEditButton, replacementTags, allReplacementTags,
    minHeight, maxHeight
}: EditorUIProps) => {
    const isPlainText = mode === 'sms' || mode === 'subject';
    const showError = !!error && !hideError;

    return (
        <RichTextEditor 
            editor={editor}
            unstyled={unstyled}
            className={`${classes.editor} ${unstyled ? classes.unstyled : ''} ${mode === 'subject' ? classes.subjectRoot : ''} ${mode === 'subject' && showError ? classes.error : ''}`}
            style={{
                borderColor: showError ? 'var(--mantine-color-yellow-6)' : undefined,
                '--editor-border-color': showError ? 'var(--mantine-color-yellow-6)' : undefined,
                borderRadius: (mode === 'subject') ? 'var(--mantine-radius-sm)' : undefined,
                minHeight: minHeight,
                maxHeight: maxHeight,
                cursor: 'text',
            }}
            bg={bg}
            onClick={(e) => {
                if (e.currentTarget.contains(e.target as Node)) {
                    editor?.commands.focus();
                }
            }}
        >
            {mode === 'email' && (
                <>
                    <RichTextEditor.Toolbar sticky stickyOffset={stickyOffset} className={classes.toolbarRoot}>
                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                            {<RichTextEditor.ColorPicker
                                colors={[
                                    '#25262b',
                                    '#868e96',
                                    '#fa5252',
                                    '#e64980',
                                    '#be4bdb',
                                    '#7950f2',
                                    '#4c6ef5',
                                    '#228be6',
                                    '#15aabf',
                                    '#12b886',
                                    '#40c057',
                                    '#82c91e',
                                    '#fab005',
                                    '#fd7e14',
                                ]}
                            />}
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                            <CustomLinkControl />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <CustomAlignLeftControl />
                            <CustomAlignCenterControl />
                            <CustomAlignJustifyControl />
                            <CustomAlignRightControl />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Undo />
                            <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>

                        {showHtmlEditButton && (
                            <RichTextEditor.ControlsGroup>
                                <HtmlEditorControl 
                                    replacementTags={allReplacementTags || replacementTags} 
                                />
                            </RichTextEditor.ControlsGroup>
                        )}
                    </RichTextEditor.Toolbar>

                    <Divider/>
                </>
            )}

            <ScrollArea.Autosize mah={maxHeight} style={{ flex: 1, maxHeight: '100%' }}>
                <RichTextEditor.Content
                    bg="transparent"
                    className={classes.content}
                />
            </ScrollArea.Autosize>
        </RichTextEditor>
    );
};

export const DEFAULT_EMAIL_MIN_HEIGHT = 250;
export const DEFAULT_SMS_MIN_HEIGHT = 250;

const ScRichTextEditorV2: React.FC<ScRichTextEditorV2Props> = ({
    value = '',
    onChange = () => {},
    mode = 'email',
    placeholder = '',
    minHeight,
    maxHeight,
    error,
    hideError = false,
    onBlur,
    showHtmlEditButton,
    stickyOffset = 0,
    setIsEmpty,
    autoFocus = false,
    replacementTags = EMPTY_TAGS,
    allReplacementTags = EMPTY_TAGS,
    bg = 'transparent',
    unstyled = false,
    ...others
}) => {
    const isPlainText = mode === 'sms' || mode === 'subject';

    const effectiveMinHeight = minHeight ?? (mode === 'email' ? DEFAULT_EMAIL_MIN_HEIGHT : mode === 'sms' ? DEFAULT_SMS_MIN_HEIGHT : undefined);
    const effectiveMaxHeight = maxHeight ?? (mode === 'subject' ? undefined : 600);

    const [suggestionState, setSuggestionState] = useState<{
        opened: boolean;
        query: string;
        range: { from: number; to: number } | null;
        coords: { top: number; left: number } | null;
    }>({
        opened: false,
        query: '',
        range: null,
        coords: null,
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const lastValueRef = useRef(value);
    const lastQueryRef = useRef('');
    const lastEmptyRef = useRef<boolean | null>(null);

    const suggestionStateRef = useRef(suggestionState);
    suggestionStateRef.current = suggestionState;
    const selectedIndexRef = useRef(selectedIndex);
    selectedIndexRef.current = selectedIndex;
    const lastMousePosRef = useRef({ x: 0, y: 0 });
    const lastKeyboardTimeRef = useRef(0);

    const handleMouseMove = useCallback((index: number, event: React.MouseEvent) => {
        const { clientX, clientY } = event;
        if (clientX === lastMousePosRef.current.x && clientY === lastMousePosRef.current.y) {
            return;
        }

        // Ignore mouse move if it happens too close to a keyboard event (likely scroll-triggered)
        if (Date.now() - lastKeyboardTimeRef.current < 50) {
            return;
        }

        lastMousePosRef.current = { x: clientX, y: clientY };
        setSelectedIndex(index);
    }, []);

    useEffect(() => {
        if (!suggestionState.opened) {
            lastMousePosRef.current = { x: 0, y: 0 };
        }
    }, [suggestionState.opened]);

    const debouncedUpdate = useDebouncedCallback(() => {
        if (!editor) return;
        
        const newVal = mode === 'email' 
            ? processOutput(editor.getHTML()) 
            : editor.getText({ blockSeparator: '\n' });
        
        if (newVal !== lastValueRef.current) {
            lastValueRef.current = newVal;
            onChange(newVal);
            
            const empty = editor.isEmpty;
            if (lastEmptyRef.current !== empty) {
                lastEmptyRef.current = empty;
                setIsEmpty?.(empty);
            }
        }
    }, 100);

    const filteredTags = useMemo(() => {
        let tags = replacementTags;
        if (suggestionState.query) {
            const q = suggestionState.query.toLowerCase();
            tags = replacementTags.filter(t => 
                t.Name.toLowerCase().includes(q) || 
                t.Description.toLowerCase().includes(q) ||
                t.Group.toLowerCase().includes(q)
            );
        }

        return [...tags].sort((a, b) => {
            const groupA = a.Group || '';
            const groupB = b.Group || '';
            const groupCompare = groupA.localeCompare(groupB);
            if (groupCompare !== 0) return groupCompare;
            return a.Name.localeCompare(b.Name);
        });
    }, [replacementTags, suggestionState.query]);

    const filteredTagsRef = useRef(filteredTags);
    filteredTagsRef.current = filteredTags;

    const suggestionListRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (suggestionState.opened && suggestionListRef.current) {
            const activeItem = suggestionListRef.current.querySelector(`[data-index="${selectedIndex}"]`);
            if (activeItem) {
                activeItem.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex, suggestionState.opened, filteredTags]);

    const extensions = useMemo(() => {
        return [
            StarterKit.configure({
                heading: mode === 'email' ? {} : false,
                blockquote: mode === 'email' ? {} : false,
                bold:  mode === 'email' ? {} : false,
                italic:  mode === 'email' ? {} : false,
                strike: false,
                code: false,
                codeBlock: false,
                bulletList:  mode === 'email' ? {} : false,
                orderedList:  mode === 'email' ? {} : false,
                horizontalRule: false,
                paragraph: mode === 'email' ? false : {},
            }),
            ...(mode === 'email' ? [
                CustomParagraph,
                Underline,
                CustomTextStyle,
                Color,
                Link.extend({
                    inclusive: false,
                }).configure({
                    openOnClick: false,
                    autolink: true,
                }),
                CustomImage.configure({
                    allowBase64: true,
                    inline: true,
                }),
                CustomTable.configure({
                    resizable: true,
                }),
                CustomTableRow,
                CustomTableHeader,
                CustomTableCell,
                Highlight,
                Subscript,
                Superscript,
                FontFamily,
            ] : []),
            ReplacementTag.configure({
                allowedNames: (allReplacementTags.length > 0 ? allReplacementTags : replacementTags).map(t => t.Name)
            }),
            Placeholder.configure({
                placeholder,
                showOnlyCurrent: false,
                emptyEditorClass: 'is-editor-empty',
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph', 'blockquote'],
            }),
            ...(mode === 'subject' ? [DisableEnter] : []),
        ];
    }, [mode, replacementTags, allReplacementTags, placeholder]);

    const checkSuggestions = useCallback((editor: any) => {
        if (!editor) return;
        const { from } = editor.state.selection;
        const textBefore = editor.state.doc.textBetween(Math.max(0, from - 20), from, undefined, "\n");
        const match = textBefore.match(/@(\w*)$/);

        if (match) {
            const query = match[1];
            const coords = editor.view.coordsAtPos(from);
            const range = { from: from - match[0].length, to: from };

            setSuggestionState(prev => {
                if (prev.opened && 
                    prev.query === query && 
                    prev.range?.from === range.from && 
                    prev.range?.to === range.to &&
                    prev.coords?.top === coords.top &&
                    prev.coords?.left === coords.left
                ) {
                    return prev;
                }
                return {
                    opened: true,
                    query,
                    range,
                    coords,
                };
            });
            
            if (query !== lastQueryRef.current) {
                setSelectedIndex(0);
                lastQueryRef.current = query;
            }
        } else {
            setSuggestionState(prev => prev.opened ? { ...prev, opened: false, query: '', range: null, coords: null } : prev);
            lastQueryRef.current = '';
        }
    }, []);

    const handleSelectTagRef = useRef<(tag: ReplacementTagItem) => void>();

    const editor = useEditor({
        extensions,
        content: convertTagsToHtml(value, isPlainText, allReplacementTags.length > 0 ? allReplacementTags : replacementTags),
        autofocus: autoFocus,
        shouldRerenderOnTransaction: true,
        onUpdate: ({ editor }) => {
            debouncedUpdate();
            checkSuggestions(editor);
        },
        onSelectionUpdate: ({ editor }) => {
            checkSuggestions(editor);
        },
        onBlur: () => {
            debouncedUpdate.flush();
            onBlur?.();
            setSuggestionState(prev => prev.opened ? { ...prev, opened: false, query: '', range: null, coords: null } : prev);
        },
        editorProps: {
            attributes: {
                class: `${classes.proseMirror} ${classes[mode]} ${unstyled ? classes.unstyled : ''}`,
                style: `background-color: transparent;`,
            },
            handleKeyDown: (view, event) => {
                const state = suggestionStateRef.current;
                const filtered = filteredTagsRef.current;
                const index = selectedIndexRef.current;

                if (state.opened && filtered.length > 0) {
                    if (event.key === 'ArrowDown') {
                        lastKeyboardTimeRef.current = Date.now();
                        setSelectedIndex(prev => (prev + 1) % filtered.length);
                        return true;
                    }
                    if (event.key === 'ArrowUp') {
                        lastKeyboardTimeRef.current = Date.now();
                        setSelectedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
                        return true;
                    }
                    if (event.key === 'Enter' || event.key === 'Tab') {
                        const tag = filtered[index];
                        if (tag) {
                            handleSelectTagRef.current?.(tag);
                            return true;
                        }
                    }
                    if (event.key === 'Escape') {
                        setSuggestionState(prev => ({ ...prev, opened: false }));
                        return true;
                    }
                }
                return false;
            }
        }
    }, [extensions]); // Add extensions here so it re-initializes if they change

    const handleSelectTag = useCallback((tag: ReplacementTagItem) => {
        if (editor && suggestionStateRef.current.range) {
            editor.chain()
                .focus()
                .deleteRange(suggestionStateRef.current.range)
                .insertContent({
                    type: 'replacementTag',
                    attrs: { name: tag.Name }
                })
                .insertContent(' ')
                .run();
            setSuggestionState(prev => ({ ...prev, opened: false }));
        }
    }, [editor]);
    handleSelectTagRef.current = handleSelectTag;

    // Update content if value changes externally (e.g. template insertion)
    useEffect(() => {
        if (!editor || value === lastValueRef.current) return;

        const currentContent = mode === 'email' 
            ? processOutput(editor.getHTML()) 
            : editor.getText({ blockSeparator: '\n' });

        if (value !== currentContent) {
            // Only update if not focused OR if it's a significant change (like template insertion)
            if (!editor.isFocused || Math.abs(value.length - currentContent.length) > 20) {
                editor.commands.setContent(convertTagsToHtml(value, isPlainText, allReplacementTags.length > 0 ? allReplacementTags : replacementTags), false);
            }
        }
        lastValueRef.current = value;
    }, [value, editor, mode, isPlainText, replacementTags]);

    return (
        <Box pos="relative" {...others} className={classes.root}>
            <EditorUI 
                editor={editor}
                mode={mode}
                unstyled={unstyled}
                error={error || null}
                hideError={hideError}
                bg={bg}
                stickyOffset={stickyOffset}
                showHtmlEditButton={!!showHtmlEditButton}
                replacementTags={replacementTags}
                allReplacementTags={allReplacementTags}
                minHeight={effectiveMinHeight}
                maxHeight={effectiveMaxHeight}
            />

            {suggestionState.opened && suggestionState.coords && (
                <Portal>
                    <Box
                        style={{
                            position: 'fixed',
                            top: suggestionState.coords.top - 15,
                            left: suggestionState.coords.left + 10,
                            zIndex: 1000,
                        }}
                    >
                        <Popover opened={true} position={'bottom-start'} withinPortal shadow="md">
                            <Popover.Target>
                                <Box style={{ width: 1, height: 1 }} />
                            </Popover.Target>
                            <Popover.Dropdown p={0} onMouseDown={(e) => e.preventDefault()}>
                                <ScrollArea.Autosize mah={300} w={300}>
                                    <Box ref={suggestionListRef}>
                                        {filteredTags.length === 0 ? (
                                            <Text p="xs" size="sm" c="dimmed">No tags found</Text>
                                        ) : (
                                            (() => {
                                                let lastGroup = '';
                                                return filteredTags.map((tag, index) => {
                                                    const currentGroup = tag.Group || 'General';
                                                    const showHeader = currentGroup !== lastGroup;
                                                    lastGroup = currentGroup;
                                                    return (
                                                        <React.Fragment key={tag.Name}>
                                                            {showHeader && (
                                                                <Box
                                                                    py={4}
                                                                    px="xs"
                                                                    // bg="gray.1"
                                                                    // style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                                                                >
                                                                    <Text size="sm" fw={400} c="dimmed">
                                                                        {currentGroup}
                                                                    </Text>
                                                                </Box>
                                                            )}
                                                            <Box
                                                                data-index={index}
                                                                p="xs"
                                                                style={{
                                                                    backgroundColor: index === selectedIndex ? 'var(--mantine-color-scBlue-0)' : undefined,
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid var(--mantine-color-gray-1)',
                                                                }}
                                                                onClick={() => handleSelectTag(tag)}
                                                                onMouseMove={(e) => handleMouseMove(index, e)}
                                                            >
                                                                <Group justify="space-between" gap="xs">
                                                                    <Box style={{ flex: 1 }}>
                                                                        {tag.Description || tag.Name}
                                                                    </Box>
                                                                    <ReplacementTagBadge mr={4}>
                                                                        {tag.Name}
                                                                    </ReplacementTagBadge>
                                                                </Group>
                                                            </Box>
                                                        </React.Fragment>
                                                    );
                                                });
                                            })()
                                        )}
                                    </Box>
                                </ScrollArea.Autosize>
                            </Popover.Dropdown>
                        </Popover>
                    </Box>
                </Portal>
            )}

            {!hideError && error && (
                <Text mt={5} size="11px" c="yellow.6">
                    {error}
                </Text>
            )}
        </Box>
    );
};

export default ScRichTextEditorV2;
